import {
  EXCEPTION_STATUS_LABEL,
  guidanceLabel,
  type ComplianceSummaryItem,
  type ComplianceScope,
} from '@bartizo/shared';
import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { listComplianceExceptions } from '../api/compliance.js';
import { useCurrentUser } from '../auth/session-context.js';
import { formatInstant, formatRelative } from '../lib/time.js';
import { Alert } from '../ui/Alert.js';
import { Button } from '../ui/Button.js';
import { Card } from '../ui/Card.js';
import { DataTable, type Column } from '../ui/DataTable.js';
import { Loading } from '../ui/Skeleton.js';
import { useRemoveDecision } from './use-remove-decision.js';

/**
 * What the failing checks of a standard are, and who decides (P11.20), the way a
 * vulnerability or posture tool triages them: what Bartizo can fix, what needs a
 * person (and why), and what a person has set aside — with the raw score always
 * shown next to the adjusted one, and a trend so one number is not read as a grade.
 */

const dayText = (iso: string): string =>
  new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });

const trendText = (
  point: { at: string; from: number | null; to: number | null } | null,
  label: string,
): string | null => {
  const from = point?.from ?? null;
  const to = point?.to ?? null;
  if (from === null || to === null) return null;
  const delta = to - from;
  if (delta === 0) return `No change ${label}`;
  return `${delta > 0 ? '+' : '−'}${String(Math.abs(delta))} ${Math.abs(delta) === 1 ? 'point' : 'points'} ${label}`;
};

export function PosturePanel({ item }: { item: ComplianceSummaryItem }): ReactNode {
  const { posture, score } = item;
  const excluded =
    posture.excluded.acceptedRisk + posture.excluded.notApplicable + posture.excluded.falsePositive;
  const since = trendText(
    posture.trend.since,
    posture.trend.since === null ? '' : `since ${dayText(posture.trend.since.at)}`,
  );
  const week = trendText(posture.trend.week, 'in the last week');
  const trend = [since, week].filter((line): line is string => line !== null);

  return (
    <Card
      title="What to do about the failing checks"
      description="A benchmark is a baseline, not a grade: a fresh, unhardened server often scores in the 40s. Most of what fails is a decision for you, not damage."
    >
      <dl className="stat-tiles">
        <div className="stat-tile">
          <dt>Bartizo can fix</dt>
          <dd>{posture.fixable}</dd>
          <dd className="cell-sub">Safe, repeatable; apply from this page.</dd>
        </div>
        <div className={`stat-tile ${posture.needsPerson > 0 ? 'stat-tile-alert' : ''}`}>
          <dt>Needs a person</dt>
          <dd>{posture.needsPerson}</dd>
          <dd className="cell-sub">
            {posture.planned > 0
              ? `${String(posture.planned)} with a fix planned`
              : 'A decision or a change only you can make.'}
          </dd>
        </div>
        <div className="stat-tile">
          <dt>Set aside by a person</dt>
          <dd>{excluded}</dd>
          <dd className="cell-sub">
            {excluded === 0
              ? 'Accepted risk, not applicable or false positive.'
              : `${String(posture.excluded.acceptedRisk)} accepted · ${String(posture.excluded.notApplicable)} not applicable · ${String(posture.excluded.falsePositive)} false positive`}
          </dd>
        </div>
      </dl>

      <p className="text-sm">
        <strong>{score === null ? 'Nothing judged' : `${String(score)}% of checks pass`}</strong>
        {excluded > 0 && posture.adjustedScore !== null && (
          <>
            {' · '}
            <span title="The score with the checks a person set aside left out. The raw score above is unchanged.">
              {String(posture.adjustedScore)}% adjusted for {String(excluded)} set aside
            </span>
          </>
        )}
        {trend.length > 0 && <span className="muted"> · {trend.join(' · ')}</span>}
        {trend.length === 0 && (
          <span className="muted"> · Trends appear after the next day’s scan.</span>
        )}
      </p>

      {posture.byCategory.length > 0 && (
        <>
          <h4>Why they need a person</h4>
          <ul className="badge-list">
            {posture.byCategory.map((each) => (
              <li key={each.category}>
                <span className="badge badge-muted">
                  {guidanceLabel(each.category)} · {each.count}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}
    </Card>
  );
}

/** Everything a person decided in the servers shown, live first and expired last. */
export function DecisionsCard({
  scope,
  timezone,
}: {
  scope: ComplianceScope;
  timezone: string;
}): ReactNode {
  const isAdmin = useCurrentUser().user.role === 'admin';
  const removal = useRemoveDecision();
  const found = useQuery({
    queryKey: ['compliance-exceptions', scope],
    queryFn: () => listComplianceExceptions(scope),
  });
  if (found.isPending) return <Loading />;
  if (found.isError) {
    return (
      <Alert tone="danger" title="Could not load the decisions">
        {found.error instanceof Error ? found.error.message : 'Try again in a moment.'}
      </Alert>
    );
  }
  const expired = found.data.items.filter((each) => !each.active).length;

  const columns: Column<(typeof found.data.items)[number]>[] = [
    {
      key: 'check',
      header: 'Check',
      rowHeader: true,
      cell: (row) => (
        <>
          {row.title}
          <span className="cell-sub">{row.policyId}</span>
        </>
      ),
      sortValue: (row) => row.title,
    },
    {
      key: 'server',
      header: 'Server',
      cell: (row) => row.serverName,
      sortValue: (row) => row.serverName,
    },
    {
      key: 'decision',
      header: 'Decision',
      cell: (row) => (
        <>
          <span className={`badge ${row.active ? 'badge-muted' : 'badge-danger'}`}>
            {EXCEPTION_STATUS_LABEL[row.status]}
            {!row.active && ' · expired'}
          </span>
          <span className="cell-sub">{row.reason}</span>
        </>
      ),
      sortValue: (row) => row.status,
    },
    {
      key: 'by',
      header: 'By',
      wideOnly: true,
      cell: (row) => row.by ?? '—',
      sortValue: (row) => row.by ?? '',
    },
    {
      key: 'ends',
      header: 'Review by',
      wideOnly: true,
      cell: (row) =>
        row.expiresAt === null ? (
          'No end date'
        ) : (
          <span title={formatInstant(row.expiresAt, { timezone })}>
            {formatRelative(row.expiresAt)}
          </span>
        ),
      sortValue: (row) => row.expiresAt ?? '9999',
    },
    ...(isAdmin
      ? [
          {
            key: 'remove',
            header: 'Remove',
            cell: (row: (typeof found.data.items)[number]) => (
              <Button
                variant="ghost"
                disabled={removal.pending}
                onClick={() => {
                  removal.remove(row.id);
                }}
              >
                Remove
              </Button>
            ),
          } satisfies Column<(typeof found.data.items)[number]>,
        ]
      : []),
  ];

  return (
    <Card
      title="Decisions"
      description="What people decided about failing checks. A decision with an end date counts as failing again after it, flagged here for review; an admin records and removes them, and each is in the audit log."
      {...(expired > 0
        ? {
            aside: (
              <span className="badge badge-danger">
                {expired} {expired === 1 ? 'needs' : 'need'} review
              </span>
            ),
          }
        : {})}
    >
      <DataTable
        caption="Decisions about failing checks"
        columns={columns}
        rows={found.data.items}
        rowKey={(row) => row.id}
        pagination={{}}
        empty={
          <p className="muted text-sm">
            Nothing recorded. Open a failing check and use “Record a decision” to accept a risk,
            mark it not applicable, or note a fix is planned.
          </p>
        }
      />
    </Card>
  );
}
